import { useContext, useMemo } from "react";
import { LanguageContext } from "../components/LanguageContext";

/**
 * Returns a MUI X DataGrid `localeText` object built from the current
 * LanguageContext. Re-runs when the language switches, so the grid's
 * "No rows", pagination, column menu, filter panel, etc. follow the app
 * locale automatically.
 */
export const useDataGridLocaleText = () => {
    const { translations: t, language } = useContext(LanguageContext);

    return useMemo(() => ({
        // Overlays
        noRowsLabel: t.dgNoRows,
        noResultsOverlayLabel: t.dgNoResults,

        // Footer
        footerRowSelected: (count) =>
            count !== 1
                ? `${count.toLocaleString()} ${t.dgFooterRowsSelected}`
                : `${count.toLocaleString()} ${t.dgFooterRowSelected}`,
        footerTotalRows: t.dgFooterTotalRows,
        footerTotalVisibleRows: (visibleCount, totalCount) =>
            `${visibleCount.toLocaleString()} / ${totalCount.toLocaleString()}`,

        // Pagination (MuiTablePagination underneath)
        MuiTablePagination: {
            labelRowsPerPage: t.dgRowsPerPage,
            labelDisplayedRows: ({ from, to, count }) =>
                `${from}–${to} ${t.dgOf} ${count !== -1 ? count : `${t.dgMoreThan} ${to}`}`,
        },

        // Column header
        columnHeaderSortIconLabel: t.dgSort,

        // Column menu
        columnMenuLabel: t.dgColumnMenu,
        columnMenuShowColumns: t.dgColumnMenuShowColumns,
        columnMenuManageColumns: t.dgColumnMenuShowColumns,
        columnMenuFilter: t.dgColumnMenuFilter,
        columnMenuHideColumn: t.dgColumnMenuHide,
        columnMenuUnsort: t.dgColumnMenuUnsort,
        columnMenuSortAsc: t.dgColumnMenuSortAsc,
        columnMenuSortDesc: t.dgColumnMenuSortDesc,

        // Filter panel
        filterPanelAddFilter: t.dgFilterAdd,
        filterPanelDeleteIconLabel: t.delete,
        filterPanelOperator: t.dgFilterOperator,
        filterPanelOperatorAnd: t.dgFilterAnd,
        filterPanelOperatorOr: t.dgFilterOr,
        filterPanelColumns: t.dgFilterColumns,
        filterPanelInputLabel: t.dgFilterValue,
        filterPanelInputPlaceholder: t.dgFilterValuePlaceholder,
        filterOperatorContains: t.dgOpContains,
        filterOperatorEquals: t.dgOpEquals,
        filterOperatorStartsWith: t.dgOpStartsWith,
        filterOperatorEndsWith: t.dgOpEndsWith,
        filterOperatorIs: t.dgOpIs,
        filterOperatorNot: t.dgOpNot,
        filterOperatorAfter: t.dgOpAfter,
        filterOperatorOnOrAfter: t.dgOpOnOrAfter,
        filterOperatorBefore: t.dgOpBefore,
        filterOperatorOnOrBefore: t.dgOpOnOrBefore,
        filterOperatorIsEmpty: t.dgOpIsEmpty,
        filterOperatorIsNotEmpty: t.dgOpIsNotEmpty,
        filterOperatorIsAnyOf: t.dgOpIsAnyOf,

        // Toolbar (in case it's enabled later)
        toolbarColumns: t.dgToolbarColumns,
        toolbarFilters: t.dgToolbarFilters,
        toolbarDensity: t.dgToolbarDensity,
        toolbarExport: t.dgToolbarExport,

        // Boolean cell
        booleanCellTrueLabel: t.dgYes,
        booleanCellFalseLabel: t.dgNo,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [language]);
};